import React, { useRef, lazy, Suspense, useState, useEffect } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import config from 'config'
import { Helmet } from 'react-helmet'
import { LocaleContext } from '../../contexts/locale/index'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { ProgressSteps } from 'components/progressSteps/component'
import { ErrorBoundary } from 'components/errorBoundary'
import { ScrollToTopOnMount } from 'components/scrollToTop'

import { ClubsList } from '../clubsList'
import { PastMatches } from '../pastMatches'
import { ClubSelection } from '../clubSelection'
import { Article } from 'components/article'
import { TeamSelection } from '../teamSelection'

import de from './locales/de.yml'
import fr from './locales/fr.yml'

const TEXTS = {
  de: de,
  fr: fr
}

const ARTICLE_IDS = {
  de: '242186732090',
  fr: '399114574647'
}

export function AppComponent() {
  const [locale, setLocale] = useState('de')
  const [isLoading, setIsLoading] = useState(false)
  const [club, setClub] = useState({
    id: null,
    region: null
  })

  const t = TEXTS[locale]
  const ARTICLE_ID = ARTICLE_IDS[locale]
  const history = useHistory()
  if (process.env.NODE_ENV !== 'test') {
    const location = useLocation()

    useEffect(() => {
      console.log(location.pathname)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'Interactions',
        event_action: 'input',
        event_label: `route:${location.pathname}`
      })
    }, [location])
  }

  // First step
  const handleClubSelection = (clubId: number, region: string) => {
    console.log('HANDLE CLUB SELECTION')
    setClub({
      id: clubId,
      region: region
    })

    // this.props.history.push(config.routes.teamSelection + region + '/' + clubId)
    history.push(config.routes.teamSelection + region + '/' + clubId)
  }

  // Second step, if there is more than one team
  const handleTeamSelection = (teamId: number) => {
    console.log('HANDLE TEAM SELECTION')
    // this.props.history.push(config.routes.articleLastMatch + teamId)
    history.push(config.routes.articleLastMatch + teamId)
  }

  return (
    <div className='index'>
      <LocaleContext.Provider value={locale}>
        <Helmet>
          <html lang={locale + '-CH'} />
        </Helmet>

        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1>
                {t.title}
                <div className='ui beta label green'>
                  <a href='#methodik'>Beta</a>
                </div>
              </h1>
              <h2>{t.lead}</h2>
            </div>
          </div>
        </header>

        <ProgressSteps />

        <main
          className={`ui green vertical infographic segment ${
            isLoading ? 'loading' : ''
          }`}
          id='main-content'
        >
          <>
            <ErrorBoundary>
              <Suspense
                fallback={
                  <div className='ui placeholder'>
                    <div className='header'>
                      <div className='line' />
                      <div className='line' />
                    </div>
                  </div>
                }
              >
                <Switch>
                  <Route
                    exact
                    path='/'
                    render={props => (
                      <div className='ui container'>
                        <ClubSelection
                          {...props}
                          handleClubSelection={handleClubSelection}
                        />
                      </div>
                    )}
                  />
                  <Route
                    path={config.routes.allTeams}
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <div className='ui container'>
                          <ClubsList
                            handleTeamSelection={handleTeamSelection}
                            {...props}
                          />
                        </div>
                      </>
                    )}
                  />
                  <Route
                    path={config.routes.matchesHistory + ':teamId'}
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <div className='ui container'>
                          <PastMatches
                            handleClubSelection={handleClubSelection}
                            handleTeamSelection={handleTeamSelection}
                            {...props}
                          />
                        </div>
                      </>
                    )}
                  />
                  <Route
                    path={
                      config.routes.matchesLeagueHistory +
                      ':leagueName/:gender/:association/:date'
                    }
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <div className='ui container'>
                          <PastMatches
                            handleClubSelection={handleClubSelection}
                            handleTeamSelection={handleTeamSelection}
                            {...props}
                          />
                        </div>
                      </>
                    )}
                  />
                  <Route
                    path={
                      config.routes.teamSelection +
                      ':associationId' +
                      '/' +
                      ':clubId'
                    }
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <div className='ui container'>
                          <TeamSelection
                            clubId={club.id}
                            region={club.region}
                            handleTeamSelection={handleTeamSelection}
                            {...props}
                          />
                        </div>
                      </>
                    )}
                  />
                  <Route
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <Article
                          setLoading={() => {
                            setIsLoading(true)
                          }}
                          unsetLoading={() => {
                            setIsLoading(false)
                          }}
                          handleClubSelection={handleClubSelection}
                          {...props}
                        />
                      </>
                    )}
                    path={config.routes.articleLastMatch + ':teamId'}
                  />
                  <Route
                    render={props => (
                      <>
                        <ScrollToTopOnMount />
                        <Article
                          setLoading={() => {
                            setIsLoading(true)
                          }}
                          unsetLoading={() => {
                            setIsLoading(false)
                          }}
                          handleClubSelection={handleClubSelection}
                          {...props}
                        />
                      </>
                    )}
                    path={config.routes.articleMatch + ':matchId'}
                  />
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </>
        </main>

        <aside className='ui blue secondary vertical segment' id='methodik'>
          <div className='ui text container'>
            <h2 className='ui infographic small grey header'>
              <i className='blue info circle icon' />
              <div className='content'>{t.methodologyTitle}</div>
            </h2>
            <div dangerouslySetInnerHTML={{ __html: t.methodologyText }} />
          </div>
        </aside>

        <Footer
          questions={[
            t.feedbackQuestionOne,
            t.feedbackQuestionTwo,
            t.feedbackQuestionThree
          ]}
          mailTo='atm@tamedia.ch'
          hidePortfolioTeaser
          articleId={ARTICLE_ID}
          credits={t.footerCredits}
        />
      </LocaleContext.Provider>
    </div>
  )
}
